/**
 * CS: Inline search panel — slides under the header instead of a dialog modal.
 * Dispatches 'dialog:open' / 'dialog:close' events so predictive-search.js works unchanged.
 */
import { Component } from '@theme/component';

class CsSearchPanel extends Component {
  #isOpen = false;
  #abortController = null;

  open() {
    if (this.#isOpen) return;
    this.#isOpen = true;

    // Close menu drawer if open — mutual exclusivity prevents z-index stacking conflict
    const drawer = document.querySelector('header-drawer');
    if (drawer?.isOpen && typeof drawer.close === 'function') {
      drawer.close();
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Position panel flush below the full header (announcement bar + nav)
    const header = document.querySelector('#header-component') || document.querySelector('header');
    if (header) {
      const bottom = header.getBoundingClientRect().bottom;
      this.style.setProperty('--panel-top', `${bottom}px`);
    }

    // Show panel
    this.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.classList.add('is-open');
    });

    // Lock body scroll
    document.documentElement.classList.add('cs-search-panel-open');

    // Dispatch dialog:open — predictive-search.js listens for this to load empty state
    this.dispatchEvent(new CustomEvent('dialog:open'));

    // Focus the search input after animation frame
    requestAnimationFrame(() => {
      const input = this.querySelector('[ref="searchInput"]');
      if (input instanceof HTMLInputElement) input.focus();
    });

    // Escape key closes panel
    document.addEventListener('keydown', this.#onKeyDown, { signal });

    // Backdrop click closes panel
    const backdrop = this.querySelector('[data-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close(), { signal });
    }
  }

  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;

    this.classList.remove('is-open');
    this.classList.add('is-closing');

    // Unlock body scroll
    document.documentElement.classList.remove('cs-search-panel-open');

    // Dispatch dialog:close — predictive-search.js listens for this to reset search
    this.dispatchEvent(new CustomEvent('dialog:close'));

    // Clean up event listeners
    this.#abortController?.abort();
    this.#abortController = null;

    // Wait for slide-up animation, then hide
    const container = this.querySelector('.cs-search-panel__container');
    const done = () => {
      if (this.#isOpen) return; // re-opened during animation
      this.classList.remove('is-closing');
      this.setAttribute('hidden', '');
    };
    container?.addEventListener('transitionend', done, { once: true });

    // Fallback if transitionend doesn't fire
    setTimeout(() => {
      if (!this.#isOpen) done();
    }, 400);
  }

  /** Alias for predictive-search.js CMD+K compatibility */
  toggleDialog() {
    if (this.#isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };
}

if (!customElements.get('cs-search-panel')) {
  customElements.define('cs-search-panel', CsSearchPanel);
}
